import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { ApolloCache, FetchResult } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import {
  CreateTimeSignalMutation,
  DiariesForDateRangeDocument,
  Label,
  LabelType,
  PutLabelSuggestionsMutation,
  SortOrder,
  TimeSignalClassification,
  useCreateTimeSignalMutation,
  usePutLabelSuggestionsMutation
} from 'graphql/types';
import { mapDateToISOString } from 'utils/date-time';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import {
  TimeSignalGetParam,
  useTimeSignalGetParam
} from 'modules/notes/hooks/useTimeSignalParam';
import { useNotesToastContext } from 'modules/notes/NotesToastContextProvider';
import {
  NOTES_PAGE_TYPE,
  NotesClassification,
  useNotesFilterContext
} from 'modules/notes/NotesFilterContextProvider';
import { SearchCriteria } from 'modules/notes/common';
import { useMeContext } from 'modules/me';
import { TIMELINE_TIMESIGNAL_COUNT } from 'modules/notes/Timeline';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks/useIsSmartSuggestionSettingEnabled';
import {
  updateCachedTimeSignalsWithFederatedSuggestions,
  updateCreateSignalCache
} from 'modules/notes/cache/utils/cache-utils';
import { useNotesSortContext } from 'modules/notes/NotesSortContextProvider';
import { useNotesAnimationContext } from 'modules/notes/providers';
import { NotesActions } from 'modules/notes/notesSelector/hooks';
import { clearCachedDates } from 'modules/notes/cache/utils/time-signal-utils';
import { routePathMap } from 'modules/common';
import { useSmartTimesheetContext } from 'modules/smartDiary/smartDiaryNotes';
import { useFeatureFlag } from 'modules/common/hooks';
import { splitDateRanges } from 'modules/smartDiary/smartDiaryNotes/utils/date-range-split';
import { NoteData } from '../../common/noteSchema';
import { mapTimesheetLabel, truncateMessage } from '../../common/utils';

export const updateCreateCache = (
  param: TimeSignalGetParam,
  currentClassification: NotesClassification,
  searchCriteria: SearchCriteria,
  sortOrder: SortOrder
) => (
  cache: ApolloCache<CreateTimeSignalMutation>,
  mutationResult: FetchResult<
    CreateTimeSignalMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.createTimeSignal?.timeSignal) {
    updateCreateSignalCache(
      cache,
      mutationResult?.data?.createTimeSignal?.timeSignal,
      param,
      currentClassification,
      searchCriteria,
      sortOrder
    );
  }
};

const updateCacheWithFederatedSuggestions = (param: TimeSignalGetParam) => (
  cache: ApolloCache<CreateTimeSignalMutation>,
  mutationResult: FetchResult<
    PutLabelSuggestionsMutation,
    Record<string, any>,
    Record<string, any>
  >
): void => {
  if (mutationResult?.data?.putLabelSuggestions) {
    updateCachedTimeSignalsWithFederatedSuggestions(
      cache,
      mutationResult?.data?.putLabelSuggestions,
      param
    );
  }
};

export const getTime = (
  sourceDate: Date,
  targetDate: Date,
  ianaName?: string
): Date | null => {
  if (!targetDate) {
    return null;
  }

  const sourceDateTime = DateTime.fromJSDate(sourceDate);
  const targetDateTime = DateTime.fromJSDate(targetDate);

  const time = DateTime.fromObject(
    {
      day: sourceDateTime.day,
      month: sourceDateTime.month,
      year: sourceDateTime.year,
      hour: targetDateTime.hour,
      minute: targetDateTime.minute,
      second: targetDateTime.second
    },
    { zone: ianaName ?? 'utc' }
  );

  return time.toJSDate();
};

const acceptSuggestion = (labels: TimesheetLabel[]): TimesheetLabel[] => {
  return labels.map((label) => {
    if (
      !label.value?.idValue &&
      !label.value?.stringValue &&
      !label.value?.numericValue
    ) {
      return {
        ...label,
        value: label.suggestedValues?.[0]
      };
    }

    return label;
  });
};

export const useCreateTimeSignal = ({
  onClose
}: {
  onClose: () => void;
}): { onAddNote: (note: NoteData) => Promise<void>; loading: boolean } => {
  const {
    filterContext: { selectedClassification, searchCriteria, pageType }
  } = useNotesFilterContext();
  const { id, locale } = useMeContext() || {};
  const { sortOrder } = useNotesSortContext();
  const ianaName = locale?.timeZone?.ianaName;
  const param = useTimeSignalGetParam(
    pageType === NOTES_PAGE_TYPE.TIMELINE
      ? TIMELINE_TIMESIGNAL_COUNT
      : undefined
  );

  const [
    addNote,
    { loading: createTimeSignalLoading, error: createTimeSignalError }
  ] = useCreateTimeSignalMutation();
  const [
    putSuggestions,
    { loading: putSuggestionsLoading, error: putSuggestionsError }
  ] = usePutLabelSuggestionsMutation();
  const { setToastContext } = useNotesToastContext();
  const { setNotesToAnimate } = useNotesAnimationContext();

  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const location = useLocation();
  const { currentPeriod } = useSmartTimesheetContext();

  const refetchQueries = useMemo(() => {
    if (location.pathname === routePathMap.notes) {
      return ['timeSignalsCount', 'timeSignalsTotals'];
    } else if (location.pathname === routePathMap.smartDiary) {
      const dateRange = {
        fromDate: currentPeriod?.startDate,
        toDate: currentPeriod?.endDate
      };

      return splitDateRanges(dateRange).map((r) => ({
        query: DiariesForDateRangeDocument,
        variables: {
          dateRange: {
            fromDate: r.fromDate,
            toDate: r.toDate
          }
        }
      }));
    }
    return [];
  }, [currentPeriod?.endDate, currentPeriod?.startDate, location.pathname]);

  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const me = useMeContext();
  const onAddNote = useCallback(
    async (note: NoteData) => {
      if (
        (!createTimeSignalLoading && createTimeSignalError) ||
        (!putSuggestionsLoading && putSuggestionsError)
      ) {
        setToastContext({
          showError: true,
          message: 'toast.note.failure'
        });
      }
      let startTime = note.startTime;
      let endTime = note.endTime;
      if (note.date && startTime) {
        startTime = getTime(note.date, startTime, ianaName);
      }
      if (note.date && endTime) {
        endTime = getTime(note.date, endTime, ianaName);
      }

      let labels: Label[] = [
        ...(note.timesheetLabels ?? []),
        ...(note.customLabels ?? [])
      ];
      const classification =
        note.classification ?? TimeSignalClassification.Unclassified;
      if (
        isTimeSignalFederatedSuggestionsEnabled &&
        classification === TimeSignalClassification.Accepted
      ) {
        labels = acceptSuggestion(labels);
      }
      clearCachedDates(id);
      const addNoteResult = await addNote({
        variables: {
          input: {
            date: note.date ? DateTime.fromJSDate(note.date).toISODate() : null,
            classification,
            description: note.note,
            startTime: mapDateToISOString(startTime),
            endTime: mapDateToISOString(endTime),
            duration: note.duration ? note.duration : null,
            labelValues: labels.map((l: TimesheetLabel) =>
              mapTimesheetLabel(
                isSmartSuggestionEnabled,
                l,
                isTimeSignalFederatedSuggestionsEnabled
              )
            ),
            labelValuesInferenceId: note.labelValuesInferenceId
          }
        },
        update: updateCreateCache(
          param,
          selectedClassification,
          searchCriteria,
          sortOrder
        ),
        awaitRefetchQueries: true,
        refetchQueries: refetchQueries
      });
      const timeSignal = addNoteResult.data?.createTimeSignal?.timeSignal;
      if (
        isTimeSignalFederatedSuggestionsEnabled &&
        timeSignal?.id &&
        me?.id &&
        timeSignal?.labelValuesInferenceId
      ) {
        await putSuggestions({
          variables: {
            input: {
              userUri: me.id,
              inferenceId: timeSignal.labelValuesInferenceId,
              timeSignalId: timeSignal.id,
              startTime: mapDateToISOString(startTime),
              endTime: mapDateToISOString(endTime),
              duration: note.duration ?? null,
              suggestions: labels
                .filter(
                  (label) => Boolean(label) && label.type !== LabelType.Comment
                )
                .map(
                  (l: TimesheetLabel) =>
                    ({
                      labelId: l.id,
                      suggestionMetadata: l.suggestionMetadata,
                      thresholdMet: l.thresholdMet ?? false,
                      suggestedValues: l.suggestedValues ?? []
                    } as any)
                )
            }
          },
          update: updateCacheWithFederatedSuggestions(param)
        });
      }

      if (
        !createTimeSignalLoading &&
        !createTimeSignalError &&
        (isTimeSignalFederatedSuggestionsEnabled
          ? !putSuggestionsLoading && !putSuggestionsError
          : true)
      ) {
        setToastContext({
          showSuccess: true,
          message: `toast.note.addedTo.${classification.toLocaleLowerCase()}`,
          values: { message: truncateMessage(note.note ?? '') }
        });
        if (addNoteResult.data?.createTimeSignal.timeSignal) {
          setNotesToAnimate([
            {
              id: addNoteResult.data?.createTimeSignal.timeSignal.id,
              action: 'AddNote',
              folder: classification.toString() as NotesActions
            }
          ]);
          setTimeout(() => setNotesToAnimate([]), 2000);
        }
      }

      onClose();
    },
    [
      createTimeSignalLoading,
      createTimeSignalError,
      putSuggestionsLoading,
      putSuggestionsError,
      id,
      addNote,
      param,
      selectedClassification,
      searchCriteria,
      sortOrder,
      refetchQueries,
      isTimeSignalFederatedSuggestionsEnabled,
      me?.id,
      onClose,
      setToastContext,
      ianaName,
      isSmartSuggestionEnabled,
      putSuggestions,
      setNotesToAnimate
    ]
  );

  return {
    onAddNote,
    loading: createTimeSignalLoading || putSuggestionsLoading
  };
};
